import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';

// Piezas que un técnico escribió/escaneó y no coincidieron con ningún
// producto del catálogo local — ver LineBuilder (FinalizarVisitaModal.js) y
// getPiezasPendientesAlta.js. Oficina decide si merece la pena darlas de
// alta en StelOrder; al sincronizarse, entran solas en el catálogo.
export default function PiezasPendientesAdminView() {
  const [piezas, setPiezas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [procesando, setProcesando] = useState(null); // id en curso

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/getPiezasPendientesAlta', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al cargar');
      setPiezas(d.piezas || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const resolver = async (id, accion) => {
    setProcesando(id);
    try {
      const r = await fetch('/.netlify/functions/resolverPiezaPendiente', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id, accion }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al guardar');
      setPiezas(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(null);
    }
  };

  const fecha = s => (s ? new Date(s).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Piezas pendientes de alta</h1>
          <p className="text-sm text-gray-500">{piezas.length} referencias sin coincidencia en el catálogo, ordenadas por nº de veces pedida</p>
        </div>
        <button onClick={cargar} disabled={loading}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl">
          {loading ? '…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

      {!loading && piezas.length === 0 && !error && (
        <p className="text-sm text-gray-400 text-center py-12">🎉 Sin piezas pendientes</p>
      )}

      {piezas.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm divide-y divide-gray-50">
          {piezas.map(p => (
            <div key={p.id} className="px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 flex-shrink-0">{p.vecesPedida || 1}×</span>
                  <span className="text-sm font-mono font-semibold text-gray-800">{p.referencia}</span>
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{p.descripcion || '(sin descripción)'}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Último pedido por {p.ultimoSolicitanteEmail || '—'} · orden {p.ultimaOrdenReferencia || p.ultimaOrdenId || '—'} · {fecha(p.ultimaFecha)}
                </p>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={() => resolver(p.id, 'resuelta')}
                  disabled={procesando === p.id}
                  title="Ya se dio de alta en StelOrder"
                  className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg disabled:opacity-50"
                >
                  Dada de alta
                </button>
                <button
                  onClick={() => resolver(p.id, 'descartada')}
                  disabled={procesando === p.id}
                  title="No merece la pena darla de alta"
                  className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg disabled:opacity-50"
                >
                  Descartar
                </button>
                <button
                  onClick={() => resolver(p.id, 'borrar')}
                  disabled={procesando === p.id}
                  title="Borrar (error de tecleo)"
                  className="px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg disabled:opacity-50"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
